import { useCallback, useMemo } from 'react';
import { useClient, useMutation, useQuery } from 'urql';
import {
  BULK_ACTION_CHOFERES,
  CHOFERES_LIST,
  CHOFER_BY_ID,
  CREATE_CHOFER,
  DELETE_CHOFER_SOFT,
  RESTORE_CHOFER,
  UPDATE_CHOFER,
  UPSERT_DOCUMENT_LINKS,
} from '../graphql/choferes.gql.js';
import { withIdempotencyKey } from '@/utils/idempotency.js';

export const PAGE_SIZE_CHOFERES = 20;

export function mapChoferFilters(raw = {}) {
  if (!raw) return undefined;
  const filter = {};
  if (raw.search && raw.search.trim().length > 0) {
    filter.search = raw.search.trim();
  }
  if (raw.empresaId) {
    filter.empresaId = raw.empresaId;
  }
  if (raw.sucursalId) {
    filter.sucursalId = raw.sucursalId;
  }
  if (raw.estado) {
    const estados = Array.isArray(raw.estado) ? raw.estado : [raw.estado];
    const normalized = estados.map(value => value?.toString().toUpperCase());
    const simpleEstados = normalized.filter(value => ['ACTIVO', 'SUSPENDIDO', 'ELIMINADO'].includes(value));
    if (simpleEstados.length > 0) {
      filter.estados = simpleEstados;
    }
    if (normalized.includes('VENCIDO')) {
      filter.soloVencidos = true;
    }
  }
  if (raw.licenciaDesde) {
    filter.licenciaDesde = raw.licenciaDesde;
  }
  if (raw.licenciaHasta) {
    filter.licenciaHasta = raw.licenciaHasta;
  }
  if (raw.fechaAltaDesde) {
    filter.fechaAltaDesde = raw.fechaAltaDesde;
  }
  if (raw.fechaAltaHasta) {
    filter.fechaAltaHasta = raw.fechaAltaHasta;
  }
  if (raw.actualizadoDesde) {
    filter.actualizadoDesde = raw.actualizadoDesde;
  }
  if (raw.actualizadoHasta) {
    filter.actualizadoHasta = raw.actualizadoHasta;
  }
  if (raw.incluirEliminados) {
    filter.incluirEliminados = true;
  }
  return Object.keys(filter).length > 0 ? filter : undefined;
}

export function mapChoferOrder(order) {
  if (!order?.field) return undefined;
  return {
    field: order.field,
    direction: order.direction?.toLowerCase() === 'desc' ? 'desc' : 'asc',
  };
}

export function useChoferesList({ page = 1, pageSize = PAGE_SIZE_CHOFERES, filter, order, pause = false } = {}) {
  const variables = useMemo(
    () => ({
      page,
      pageSize,
      filter: mapChoferFilters(filter),
      order: mapChoferOrder(order),
    }),
    [page, pageSize, filter, order],
  );
  const context = useMemo(
    () => ({
      additionalTypenames: ['Chofer'],
    }),
    [],
  );

  const query = useQuery({
    query: CHOFERES_LIST,
    variables,
    pause,
    context,
  });

  return query;
}

export function useChoferById(id, { pause = false } = {}) {
  return useQuery({
    query: CHOFER_BY_ID,
    variables: { id },
    pause: !id || pause,
    context: {
      additionalTypenames: ['Chofer'],
    },
  });
}

export function useChoferMutations() {
  const client = useClient();
  const [, executeCreate] = useMutation(CREATE_CHOFER);
  const [, executeUpdate] = useMutation(UPDATE_CHOFER);
  const [, executeDelete] = useMutation(DELETE_CHOFER_SOFT);
  const [, executeRestore] = useMutation(RESTORE_CHOFER);
  const [, executeBulk] = useMutation(BULK_ACTION_CHOFERES);
  const [, executeDocs] = useMutation(UPSERT_DOCUMENT_LINKS);

  const invalidateList = useCallback(async () => {
    try {
      if (typeof client.invalidate === 'function') {
        await client.invalidate({ query: CHOFERES_LIST });
      }
    } catch (error) {
      console.warn('No se pudo invalidar la cache de choferes', error);
    }
  }, [client]);

  const create = useCallback(
    async input => {
      const result = await executeCreate({ input }, withIdempotencyKey('chofer-create'));
      if (result.error) throw result.error;
      await invalidateList();
      return result.data?.createChofer;
    },
    [executeCreate, invalidateList],
  );

  const update = useCallback(
    async (id, input) => {
      const result = await executeUpdate({ id, input }, withIdempotencyKey('chofer-update'));
      if (result.error) throw result.error;
      await invalidateList();
      try {
        if (typeof client.invalidate === 'function') {
          await client.invalidate({ query: CHOFER_BY_ID, variables: { id } });
        }
      } catch (error) {
        console.warn('No se pudo invalidar la cache del chofer', error);
      }
      return result.data?.updateChofer;
    },
    [client, executeUpdate, invalidateList],
  );

  const softDelete = useCallback(
    async id => {
      const result = await executeDelete({ id }, withIdempotencyKey('chofer-delete'));
      if (result.error) throw result.error;
      await invalidateList();
      return result.data?.deleteChoferSoft ?? false;
    },
    [executeDelete, invalidateList],
  );

  const restore = useCallback(
    async id => {
      const result = await executeRestore({ id }, withIdempotencyKey('chofer-restore'));
      if (result.error) throw result.error;
      await invalidateList();
      return result.data?.restoreChofer;
    },
    [executeRestore, invalidateList],
  );

  const bulkAction = useCallback(
    async (ids, action) => {
      const result = await executeBulk({ ids, action }, withIdempotencyKey('chofer-bulk'));
      if (result.error) throw result.error;
      await invalidateList();
      return result.data?.bulkActionChoferes;
    },
    [executeBulk, invalidateList],
  );

  const upsertDocs = useCallback(
    async (id, docs) => {
      const result = await executeDocs({ id, docs }, withIdempotencyKey('chofer-docs'));
      if (result.error) throw result.error;
      await invalidateList();
      return result.data?.upsertChoferDocumentLinks;
    },
    [executeDocs, invalidateList],
  );

  return { create, update, softDelete, restore, bulkAction, upsertDocs };
}

export default {
  useChoferesList,
  useChoferById,
  useChoferMutations,
  mapChoferFilters,
  mapChoferOrder,
};
